import { addHours } from "date-fns";
import { PlanBase } from "../models/PlanBase";
import { SubscriptionBase } from "../models/SubscriptionBase";

export const SUBSCRIPTION_END_GRACE_PERIOD_HOURS = 48;

export function hasValidPlanWith({
  cond,
  subscriptions,
}: {
  subscriptions: SubscriptionBase<any>[];
  cond: (props: {
    plan: PlanBase<any>;
    customTransactionLimit?: number;
  }) => boolean;
}): boolean {
  return getValidSubscriptions({
    subscriptions: subscriptions,
  }).some((s) =>
    cond({
      plan: s.plan,
      customTransactionLimit: s.customTransactionLimit,
    })
  );
}

export function hasValidPlanWithCapacity({
  userTransactionCount,
  subscriptions,
}: {
  subscriptions: SubscriptionBase<any>[];
  userTransactionCount: number;
}): boolean {
  return getValidSubscriptions({
    subscriptions: subscriptions,
  }).some((subscription) => {
    const limit =
      subscription.customTransactionLimit ?? subscription.plan.transactionLimit;
    return limit === -1 || userTransactionCount <= limit;
  });
}

export function getValidSubscriptions({
  subscriptions,
}: {
  subscriptions: SubscriptionBase<any>[];
}) {
  const now = new Date();

  return subscriptions.filter(
    (subscription) =>
      subscription.status == "valid" &&
      now >= subscription.begins &&
      now <= addHours(subscription.ends, SUBSCRIPTION_END_GRACE_PERIOD_HOURS)
  );
}

export function getSubscriptionStatus(subscription: SubscriptionBase<any>): {
  expired: boolean;
  cancelled: boolean;
  valid: boolean;
  endDate: Date;
} {
  const now = new Date();
  return {
    expired:
      addHours(subscription.ends, SUBSCRIPTION_END_GRACE_PERIOD_HOURS) < now,
    cancelled: !!subscription.isCancelled,
    valid: subscription.status == "valid",
    endDate: addHours(subscription.ends, SUBSCRIPTION_END_GRACE_PERIOD_HOURS),
  };
}

export const planHasCapacity = ({
  plan: { transactionLimit },
  transactionCount,
}: {
  plan: PlanBase<any>;
  transactionCount: number;
}) => transactionLimit >= transactionCount || transactionLimit === -1;

export const getCurrentSubscription = <TSub extends SubscriptionBase<any>>(
  subscriptions: TSub[]
) =>
  subscriptions.find((s) => s.current && s.stripeSubscriptionId) ??
  subscriptions.find((s) => s.current);
